import {
  calculateColumnWidth,
  mapColumnTypes,
  TYPES,
} from "@/react/components/table/columns";
import { getColumnDef } from "@/react/schema/schemaFields";
import selectColumnFactory from "@/react/components/table/components/selectColumnFactory";

import { getBaseQueueColumns } from "../all-queues/columnDefinition";
import schema from "./schema";
import { AUDIO_FILE_TYPES, AudioFileCell } from "./AudioFileCell";

/**
 * All columns will be editable by default. All non-editable columns
 * should be listed here.
 * @type {Readonly<*[]>}
 */
const READ_ONLY_COLUMNS = Object.freeze([
  schema.fields.id.spec.key,
  schema.fields.sla_notification_enabled.spec.key,
]);

/**
 * This function filters and modifies the base queue columns.
 * - Excludes the `media_type` column.
 * - Sets the `editable` property to `true` for all columns not marked as read-only,
 *  {@see READ_ONLY_COLUMNS}.
 *
 * @param {Object} schema - The current schema (must extend the common/generic queues schema).
 * @return {Array} A truncated column definition containing all common fields except `media_type`.
 */
const filteredBaseQueueColumns = (schema) => {
  const baseColumns = getBaseQueueColumns(schema);
  return baseColumns.reduce((accumulator, column) => {
    // Do NOT include the media_type column
    if (column.id === schema.fields.media_type.spec.key) {
      return accumulator;
    }

    if (READ_ONLY_COLUMNS.includes(column.id)) {
      accumulator.push(column);
      return accumulator;
    }

    accumulator.push({ ...column, editable: true });
    return accumulator;
  }, []);
};

const inboundPhoneColumnDefinition = () =>
  calculateColumnWidth(
    [
      selectColumnFactory(),
      ...filteredBaseQueueColumns(schema),
      {
        ...getColumnDef(schema.fields.whisper_audio_id, "Whisper Audio ID"),
        editable: true,
        editableCell: AudioFileCell(AUDIO_FILE_TYPES.WHISPER),
      },
      {
        ...getColumnDef(
          schema.fields.call_recording_percentage,
          "Call Recording Percentage (%)"
        ),
        type: TYPES.NUMBER,
        editable: true,
      },
      {
        ...getColumnDef(schema.fields.waiting_audio_id, "Waiting Audio ID"),
        editable: true,
        editableCell: AudioFileCell(AUDIO_FILE_TYPES.WAITING),
      },
      {
        ...getColumnDef(schema.fields.voicemail_enabled, "Enable Voicemail"),
        type: TYPES.BOOLEAN,
        editable: true,
      },
      {
        ...getColumnDef(
          schema.fields.voicemail_message_deliver,
          "Voicemail Message Deliver"
        ),
        type: TYPES.STRING,
        editable: true,
      },
      {
        ...getColumnDef(
          schema.fields.voicemail_message_main,
          "Voicemail Message Main"
        ),
        type: TYPES.STRING,
        editable: true,
      },
      {
        ...getColumnDef(
          schema.fields.voicemail_message_optional,
          "Voicemail Message Optional"
        ),
        type: TYPES.STRING,
        editable: true,
      },
      {
        ...getColumnDef(
          schema.fields.voicemail_message_required,
          "Voicemail Message Required"
        ),
        type: TYPES.STRING,
        editable: true,
      },
      {
        ...getColumnDef(schema.fields.voicemail_queue_id, "Voicemail Queue ID"),
        type: TYPES.STRING,
        editable: true,
      },
    ].map(mapColumnTypes)
  );

export default inboundPhoneColumnDefinition;
